import React, { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { useTheme } from "context/AntdThemeContext"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import {
  getDefaultCalendarColor,
  getPaymentAccentColor,
  getPrimaryCalendarTextColor,
  resolveBookingColors
} from "pagesV2/pro/Calendar/calendarColorUtils"
import { buildMultiDayRuns } from "../../timeGridUtils"
import styles from "./index.module.css"

/** "2/10" — only a trip that reports a capacity gets the label. */
const formatCapacity = (booking) => {
  const booked = booking?.total_guest_count ?? booking?.guest_count
  const capacity = booking?.max_guests
  if (booked == null || capacity == null) return ""
  return `${booked}/${capacity}`
}

/**
 * One multi-day trip, as a bar.
 *
 * A trip that began before the window opens with a chevron instead of its payment accent: with
 * the start off-screen the accent marks nothing, while the arrow says where the rest of it went.
 */
export const MultiDayBar = ({ run, onOpenBooking }) => {
  const { booking } = run
  const capacity = formatCapacity(booking)
  const { theme } = useTheme()

  // The same colours the single-day tiles use: a custom experience colour is a 10% tint rather
  // than the raw hex, which on a saturated colour left the dark label unreadable. Painted over an
  // opaque white base, as the tiles are, so the low-alpha tint does not pick up the cell behind it.
  const colors = resolveBookingColors(booking?.color, getDefaultCalendarColor(), getPrimaryCalendarTextColor(theme), getEventColor)

  return (
    <button
      type="button"
      className={styles.bar}
      style={{ backgroundImage: `linear-gradient(${colors.backgroundColor}, ${colors.backgroundColor})` }}
      onClick={() => onOpenBooking?.(booking.uuid || booking.id)}
      title={[booking.client_name, booking.experience_name || booking.title].filter(Boolean).join(" • ")}
    >
      {run.continuesBefore ? (
        <Icon name="ChevronLeft" width={12} height={12} color="var(--color-text-secondary-dark)" />
      ) : (
        <span
          className={styles.accent}
          style={{ background: getPaymentAccentColor(booking?.payment_status) || colors.borderColor }}
          aria-hidden
        />
      )}
      <span className={styles.label}>
        {booking.client_name && <span className={styles.client}>{booking.client_name} •</span>}
        <span className={styles.experience}>{booking.experience_name || booking.title}</span>
      </span>
      {capacity && <span className={styles.capacity}>{capacity}</span>}
      {run.continuesAfter && (
        <Icon name="ChevronRight" width={12} height={12} color="var(--color-text-secondary-dark)" />
      )}
    </button>
  )
}

MultiDayBar.propTypes = {
  run: PropTypes.object.isRequired,
  onOpenBooking: PropTypes.func
}

/**
 * The multi-day trips of a week, as table rows above the hour grid.
 *
 * A trip running Mon–Wed belongs to no hour, so the time grid has nowhere honest to put it — the
 * store's per-day copies would draw it three times, once in each column at its start time. The
 * design lifts them out into a strip of their own, where each trip is a single bar spanning the
 * columns it covers.
 *
 * These are `<tr>`s rather than a floating layer because the grid's day columns are not equal
 * width — each is sized to the lanes its bookings need — so `colSpan` is the only thing that
 * keeps a bar over the days it actually covers.
 *
 * One row per lane, so two overlapping trips stack instead of colliding. Collapsing drops the
 * lanes but keeps the toggle row, so the control never moves.
 */
const MultiDayStrip = ({ dayKeys, bookings, onOpenBooking, isBlackedOut, hasLead = true }) => {
  const { t } = useTranslation()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { runs, laneCount } = buildMultiDayRuns(dayKeys, bookings)

  if (!runs.length) return null

  const toggle = (
    <button
      type="button"
      className={styles.toggle}
      onClick={() => setIsCollapsed((collapsed) => !collapsed)}
      aria-expanded={!isCollapsed}
      aria-label={
        isCollapsed
          ? t("calendar.multi_day.expand", { defaultValue: "Show multi-day bookings" })
          : t("calendar.multi_day.collapse", { defaultValue: "Hide multi-day bookings" })
      }
    >
      <Icon
        name={isCollapsed ? "ChevronRight" : "ChevronDown"}
        width={20}
        height={20}
        color="var(--color-text-secondary-dark)"
      />
    </button>
  )

  if (isCollapsed) {
    return (
      <tr className={styles.row}>
        {hasLead && <td className={styles.leadCell}>{toggle}</td>}
        <td className={styles.laneCell} colSpan={dayKeys.length} />
      </tr>
    )
  }

  // Walk each lane left to right: a run claims its columns with colSpan, and the days no run
  // covers are plain cells, so every lane row still adds up to the full week.
  const renderLane = (lane) => {
    const laneRuns = runs.filter((run) => run.lane === lane)
    const cells = []
    let index = 0

    while (index < dayKeys.length) {
      const run = laneRuns.find((candidate) => candidate.startIndex === index)
      if (run) {
        cells.push(
          <td key={dayKeys[index]} className={styles.laneCell} colSpan={run.span}>
            <MultiDayBar run={run} onOpenBooking={onOpenBooking} />
          </td>
        )
        index += run.span
      } else {
        cells.push(
          <td
            key={dayKeys[index]}
            className={`${styles.laneCell} ${isBlackedOut?.(dayKeys[index]) ? styles.laneCellBlackedOut : ""}`}
          />
        )
        index += 1
      }
    }
    return cells
  }

  return (
    <>
      {Array.from({ length: laneCount }).map((_, lane) => (
        <tr key={lane} className={styles.row}>
          {hasLead && lane === 0 && (
            <td className={styles.leadCell} rowSpan={laneCount}>
              {toggle}
            </td>
          )}
          {renderLane(lane)}
        </tr>
      ))}
    </>
  )
}

MultiDayStrip.propTypes = {
  /** The visible days in order, "YYYY-MM-DD" — one column each, matching the grid's own. */
  dayKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Every booking in the window; single-day ones are ignored here. */
  bookings: PropTypes.array.isRequired,
  onOpenBooking: PropTypes.func,
  /** Shades a column whose day is closed, so the strip reads with the grid below it. */
  isBlackedOut: PropTypes.func,
  /** False where the grid has no hour-label column to host the collapse toggle — the week's
   *  non-agenda table is all day columns, so the strip there is always expanded. */
  hasLead: PropTypes.bool
}

export default MultiDayStrip
